import { z } from 'zod';

/**
 * Line-item "Custom Targeting" model. Mirrors the legacy dashboard's rule builder,
 * which is persisted as a JSON string on `targeting.ext`:
 *
 *   { op, groups: [{ op, rules: [{ key, operator, values }] }] }
 *
 * Groups are combined with the top-level `op`; rules within a group are combined
 * with the group's `op`. This is unrelated to the xp1-xp10 `customTargeting`
 * model used by the Targeting Builder.
 */

export enum CustomTargetingExtLogicalOperator {
  And = 'AND',
  Or = 'OR',
}

export enum CustomTargetingExtRuleOperator {
  In = 'in',
  Nin = 'nin',
}

export enum CustomTargetingExtField {
  Publisher = 'publisher_id',
  Placement = 'placement_id',
  Liveramp = 'liveramp',
}

export const customTargetingExtRuleSchema = z.object({
  key: z.enum(CustomTargetingExtField),
  operator: z.enum(CustomTargetingExtRuleOperator),
  values: z.array(z.string()),
});

export const customTargetingExtGroupSchema = z.object({
  op: z.enum(CustomTargetingExtLogicalOperator),
  rules: z.array(customTargetingExtRuleSchema),
});

export const customTargetingExtFormSchema = z.object({
  op: z.enum(CustomTargetingExtLogicalOperator),
  groups: z.array(customTargetingExtGroupSchema),
  preservedRaw: z.string().optional(),
});

export type CustomTargetingExtRule = z.infer<typeof customTargetingExtRuleSchema>;
export type CustomTargetingExtGroup = z.infer<typeof customTargetingExtGroupSchema>;
export type CustomTargetingExtFormValue = z.infer<typeof customTargetingExtFormSchema>;

export function createCustomTargetingExtRule(): CustomTargetingExtRule {
  return {
    key: CustomTargetingExtField.Publisher,
    operator: CustomTargetingExtRuleOperator.In,
    values: [],
  };
}

export function createCustomTargetingExtGroup(): CustomTargetingExtGroup {
  return {
    op: CustomTargetingExtLogicalOperator.And,
    rules: [createCustomTargetingExtRule()],
  };
}

export function createDefaultCustomTargetingExt(): CustomTargetingExtFormValue {
  return { op: CustomTargetingExtLogicalOperator.And, groups: [] };
}

/**
 * Parse the persisted `targeting.ext` JSON string into the form model, tolerating
 * malformed payloads and legacy field aliases. Falls back to an empty default on
 * any failure (matching the legacy behavior).
 */
export function parseCustomTargetingExt(extRaw?: string | null): CustomTargetingExtFormValue {
  if (!extRaw) return createDefaultCustomTargetingExt();

  let parsed: unknown;
  try {
    parsed = JSON.parse(extRaw);
  } catch {
    return { ...createDefaultCustomTargetingExt(), preservedRaw: extRaw };
  }

  const record = asRecord(parsed);
  const rawGroups = Array.isArray(record.groups) ? record.groups : [];

  // A rule with a key this build doesn't understand can't be represented (or
  // re-serialized) without corrupting it, so treat the whole payload like
  // malformed JSON: keep the original string untouched until it's edited.
  const hasUnsupportedKey = rawGroups.some((group) => {
    const groupRecord = asRecord(group);
    return Array.isArray(groupRecord.rules)
      ? groupRecord.rules.some((rule) => !mapKeyToField(asRecord(rule).key))
      : false;
  });
  if (hasUnsupportedKey) {
    return { ...createDefaultCustomTargetingExt(), preservedRaw: extRaw };
  }

  const groups = rawGroups.map((group) => {
    const groupRecord = asRecord(group);
    const rules = Array.isArray(groupRecord.rules)
      ? groupRecord.rules.map((rule) => {
          const ruleRecord = asRecord(rule);
          return {
            key: mapKeyToField(ruleRecord.key) ?? CustomTargetingExtField.Publisher,
            operator: normalizeRuleOperator(ruleRecord.operator),
            values: normalizeRuleValues(ruleRecord.values),
          };
        })
      : [];
    return {
      op: normalizeLogicalOperator(groupRecord.op),
      rules: rules.length ? rules : [createCustomTargetingExtRule()],
    };
  });

  return { op: normalizeLogicalOperator(record.op), groups };
}

/**
 * Serialize the form model back into the `targeting.ext` JSON string. Always
 * stringifies (even with no groups) so a cleared value round-trips and the field
 * is never silently omitted.
 */
export function serializeCustomTargetingExt(value: CustomTargetingExtFormValue): string {
  if (value.preservedRaw) return value.preservedRaw;

  return JSON.stringify({
    op: normalizeLogicalOperator(value.op),
    groups: value.groups.map((group) => ({
      op: normalizeLogicalOperator(group.op),
      rules: group.rules.map((rule) => ({
        // rule.key is already a validated CustomTargetingExtField (the form
        // schema only allows the known enum values), so no remapping needed.
        key: rule.key,
        operator: normalizeRuleOperator(rule.operator),
        values: normalizeRuleValues(rule.values),
      })),
    })),
  });
}

function asRecord(value: unknown): Record<string, unknown> {
  return typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : {};
}

function normalizeLogicalOperator(operator: unknown): CustomTargetingExtLogicalOperator {
  return operator === CustomTargetingExtLogicalOperator.Or
    ? CustomTargetingExtLogicalOperator.Or
    : CustomTargetingExtLogicalOperator.And;
}

function normalizeRuleOperator(operator: unknown): CustomTargetingExtRuleOperator {
  return operator === CustomTargetingExtRuleOperator.Nin
    ? CustomTargetingExtRuleOperator.Nin
    : CustomTargetingExtRuleOperator.In;
}

function mapKeyToField(key: unknown): CustomTargetingExtField | undefined {
  const normalized = typeof key === 'string' ? key.toLowerCase() : '';
  switch (normalized) {
    case 'publisher_id':
      return CustomTargetingExtField.Publisher;
    case 'placement_id':
      return CustomTargetingExtField.Placement;
    case 'liveramp':
    case 'liveramp_id':
    case 'liveramp_segment':
    case 'liveramp_segment_id':
      return CustomTargetingExtField.Liveramp;
    default:
      return undefined;
  }
}

function normalizeRuleValues(values: unknown): string[] {
  if (!Array.isArray(values)) return [];
  const seen = new Set<string>();
  const result: string[] = [];
  for (const value of values) {
    const normalized = normalizeRuleValue(value);
    if (normalized && !seen.has(normalized)) {
      seen.add(normalized);
      result.push(normalized);
    }
  }
  return result;
}

function normalizeRuleValue(value: unknown): string {
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number') return String(value);
  return '';
}
